import React, { useMemo, useState } from "react";
import { useCalendar, CalendarEvent } from "../hooks/useCalendar";
import { useEntityLinks } from "../hooks/useEntityLinks";
import {
  CalendarCheckIcon,
  SpinnerIcon,
  RefreshIcon,
  PlusIcon,
} from "@/components/icons";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { PanelList } from "@/components/ui/PanelList";
import { PanelFilterTabs, PanelFilterOption } from "@/components/ui/PanelFilterTabs";
import { CalendarEventModal } from "./CalendarEventModal";
import { LinkResourceModal } from "./LinkResourceModal";
import { useCreateIdeaMutation } from "@/domains/Ideas/hooks/useIdeas";
import { CalendarEventCard, daysUntilNext, nextOccurrenceYear } from "./CalendarEventCard";
import { eventToRevertOnUnlink } from "../calendarUnlinkRevert";

interface CalendarPanelProps {
  clientId: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export const CalendarPanel: React.FC<CalendarPanelProps> = ({
  clientId,
  collapsed,
  onToggleCollapse,
}) => {
  const {
    events,
    loading,
    error,
    generating,
    generate,
    createEvent,
    updateEvent,
    refresh,
  } = useCalendar({ clientId });

  const { createLink, deleteLink } = useEntityLinks(refresh);
  const createIdeaMutation = useCreateIdeaMutation(clientId);

  const [creatingIdeaIds, setCreatingIdeaIds] = useState<Set<string>>(new Set());
  const [createIdeaError, setCreateIdeaError] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("upcoming");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [linkingEvent, setLinkingEvent] = useState<CalendarEvent | null>(null);

  const handleEditEvent = (event: CalendarEvent) => {
    setEditingEvent(event);
    setModalOpen(true);
  };

  const handleAddEvent = () => {
    setEditingEvent(null);
    setModalOpen(true);
  };

  const handleLinkEvent = (event: CalendarEvent) => {
    setLinkingEvent(event);
  };

  const handleCreateIdea = async (event: CalendarEvent) => {
    setCreateIdeaError(null);
    setCreatingIdeaIds((prev) => {
      const next = new Set(prev);
      next.add(event.id);
      return next;
    });
    let step: "create" | "link" | "status" = "create";
    try {
      const idea = await createIdeaMutation.mutateAsync({
        title: event.eventName,
        concept: event.contentAngle || undefined,
      });
      step = "link";
      await createLink({
        sourceType: "calendar_event",
        sourceId: event.id,
        targetType: "idea",
        targetId: idea.id,
        year: nextOccurrenceYear(event.eventMonth, event.eventDay),
      });
      step = "status";
      await updateEvent(event.id, { status: "planned" });
    } catch {
      setCreateIdeaError(
        step === "create"
          ? "Failed to create idea."
          : step === "link"
            ? "Idea created, but linking it to the event failed."
            : "Idea created and linked, but marking the event planned failed."
      );
    } finally {
      setCreatingIdeaIds((prev) => {
        const next = new Set(prev);
        next.delete(event.id);
        return next;
      });
      // Authoritative re-sync: the PATCH response serializes links: [] and
      // wipes the just-created link from local state. Re-fetch so the event
      // shows both status "planned" and its idea link. Display-only, so a
      // failure here must not surface a step-specific chain error.
      await refresh().catch(() => {});
    }
  };

  const handleUnlink = async (linkId: string) => {
    // Removing the last idea link revokes the evidence of the plan
    // (DD-016), so the event drops back to "upcoming".
    const revertEventId = eventToRevertOnUnlink(events, linkId);
    try {
      await deleteLink(linkId);
      if (revertEventId) {
        await updateEvent(revertEventId, { status: "upcoming" });
        await refresh().catch(() => {});
      }
    } catch {
      // silent
    }
  };

  const handleModalSave = async (data: {
    event_name: string;
    event_month?: number | null;
    event_day?: number | null;
    end_month?: number | null;
    end_day?: number | null;
    movable?: boolean;
    date_year?: number | null;
    category: string;
    relevance: string;
    content_angle: string;
    planning_lead_days: number;
  }) => {
    if (editingEvent) {
      await updateEvent(editingEvent.id, data);
    } else {
      await createEvent(data);
    }
  };

  const handleGenerate = async () => {
    setGenerateError(null);
    try {
      await generate();
    } catch (err) {
      setGenerateError(
        err instanceof Error ? err.message : "Generation failed"
      );
    }
  };

  const handleUpdate = async (
    eventId: string,
    data: { status?: string; event_month?: number | null; event_day?: number | null }
  ) => {
    try {
      await updateEvent(eventId, data);
    } catch {
      // silent for now
    }
  };

  const activeEvents = events.filter((e) => e.status !== "dismissed");

  // Sort dated events by next occurrence: upcoming events first,
  // then events that already passed this year (they roll to next year).
  const datedEvents = activeEvents
    .filter((e) => e.eventMonth !== null && e.eventDay !== null)
    .sort((a, b) => {
      return daysUntilNext(a.eventMonth!, a.eventDay!)
        - daysUntilNext(b.eventMonth!, b.eventDay!);
    });
  const undatedEvents = activeEvents.filter(
    (e) => e.eventMonth === null || e.eventDay === null
  );

  const allSorted = [...datedEvents, ...undatedEvents];

  const dismissedCount = events.filter(
    (e) => e.status === "dismissed"
  ).length;

  // Upcoming is the default view — the panel is for what's coming, not an
  // archive; planned/posted/dismissed live behind their own tabs. The full
  // tab set always renders, zero counts included (DD-007).
  const filterOptions = useMemo<PanelFilterOption[]>(() => {
    const count = (status: string) =>
      events.filter((e) => e.status === status).length;
    return [
      { id: "upcoming", label: "Upcoming", count: count("upcoming") },
      { id: "planned", label: "Planned", count: count("planned") },
      { id: "posted", label: "Posted", count: count("posted") },
      { id: "dismissed", label: "Dismissed", count: count("dismissed") },
    ];
  }, [events]);

  const visibleEvents =
    statusFilter === "dismissed"
      ? events.filter((e) => e.status === "dismissed")
      : allSorted.filter((e) => e.status === statusFilter);

  const summary = useMemo(() => {
    const next = datedEvents[0];
    if (!next) return "nothing upcoming";
    const d = daysUntilNext(next.eventMonth!, next.eventDay!);
    if (d === 0) return `${next.eventName} — today`;
    return `${next.eventName} — in ${d} day${d === 1 ? "" : "s"}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  return (
    <>
      <WorkspacePanel
        id="calendar"
        title="Calendar"
        icon={<CalendarCheckIcon className="w-4 h-4" />}
        summary={summary}
        loading={loading}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        className={collapsed ? "" : "h-full"}
        bodyClassName="min-h-[48rem] max-h-[48rem]"
        subheader={
          <PanelFilterTabs
            options={filterOptions}
            activeId={statusFilter}
            onChange={setStatusFilter}
          />
        }
        actions={
          <>
            <PanelHeaderAction
              label="Add"
              icon={<PlusIcon className="w-4 h-4" />}
              onClick={handleAddEvent}
            />
            <PanelHeaderAction
              label={generating ? "Generating..." : "Generate"}
              icon={
                generating ? (
                  <SpinnerIcon className="w-4 h-4" />
                ) : (
                  <RefreshIcon className="w-4 h-4" />
                )
              }
              onClick={handleGenerate}
              variant="secondary"
              disabled={generating}
            />
          </>
        }
      >
        <div className="flex flex-col h-full gap-4">
      {generateError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {generateError}
        </div>
      )}

      {createIdeaError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {createIdeaError}
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {allSorted.length === 0 && dismissedCount === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <p className="text-sm">No upcoming events</p>
          <p className="text-xs mt-1">
            Click Generate to create events based on this client's profile
          </p>
        </div>
      ) : (
        <PanelList
          items={visibleEvents}
          keyFor={(e) => e.id}
          divided={false}
          className="flex-1 h-auto"
          empty={
            <p className="text-sm text-gray-500 py-6 text-center">
              No {statusFilter} events.
            </p>
          }
          renderItem={(event) => (
            <CalendarEventCard
              event={event}
              onUpdate={handleUpdate}
              onEdit={handleEditEvent}
              onLink={handleLinkEvent}
              onUnlink={handleUnlink}
              onCreateIdea={handleCreateIdea}
              creatingIdea={creatingIdeaIds.has(event.id)}
            />
          )}
        />
      )}

        </div>
      </WorkspacePanel>

      <CalendarEventModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingEvent(null);
        }}
        event={editingEvent}
        onSave={handleModalSave}
      />

      {linkingEvent && (
        <LinkResourceModal
          isOpen={!!linkingEvent}
          onClose={() => setLinkingEvent(null)}
          clientId={clientId}
          eventId={linkingEvent.id}
          eventName={linkingEvent.eventName}
          existingLinks={linkingEvent.links}
          onLinked={refresh}
        />
      )}
    </>
  );
};
